import React, {useRef, useState} from "react";
import {Button, Col, DatePicker, Drawer, Form, Input, InputNumber, Radio, Row} from "antd";
import {PlusOutlined} from "@ant-design/icons";
import dayjs from "dayjs";

import InputMoney from "../../../components/billings/InputMoney";
import {messageType, showMessages} from "../../../helpers/message";
import {createPromotion} from "../../../helpers/promotion";
import {greaterThanOrEqualOne, greaterThanZero, wholeNumberGreaterOrEqualToOne} from "../../../helpers/text";
import {dateFormat} from "../../../lib/time";

import "./PromotionAddDrawer.less";

const PromotionAddDrawer = ({addButtonText, setTableRefresh}) => {
    const abortControllerRef = useRef(new AbortController());
    const abortSignalRef = useRef(abortControllerRef.current.signal);
    const mobileMode = window.innerWidth <= 768;
    const [form] = Form.useForm();

    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [radioSelected, setRadioSelected] = useState('');

    const onClose = () => {
        setOpen(false);
        setRadioSelected('');
        form.resetFields();
    }

    const handleFinish = async (values) => {
        setLoading(true);
        try {
            const abortSignal = abortSignalRef.current;
            const response = await createPromotion(values, abortSignal);
            if (response) {
                onClose();
                setTableRefresh(true);
                showMessages('Promotion successfully added.', messageType.success);
            }
            setLoading(false);
        } catch (e) {
            showMessages(e.message, messageType.error);
            setLoading(false);
        }
    }

    return (
        <>
            <Button
                className="promotions-table-add-btn"
                type="primary"
                icon={<PlusOutlined/>}
                onClick={() => setOpen(true)}
            >
                {addButtonText}
            </Button>
            <Drawer
                className="drawer-wrapper"
                title="Add Promotion"
                open={open}
                onClose={onClose}
                width={mobileMode ? "100%" : "45%"}
            >
                <Form
                    form={form}
                    layout="vertical"
                    name="add-promotion"
                    onFinish={handleFinish}
                >
                    <Row className="full-width-row">
                        <Form.Item
                            name="name"
                            label="Name: "
                            rules={[{
                                required: true,
                                message: 'Please enter a name.'
                            }]}
                        >
                            <Input placeholder="Type here"/>
                        </Form.Item>
                    </Row>
                    <Row className="full-width-row">
                        <Form.Item
                            name="promo_code"
                            label="Promo Code: "
                            rules={[{required: false}]}
                        >
                            <Input placeholder="Type here"/>
                        </Form.Item>
                    </Row>
                    <Row className="full-width-row">
                        <Form.Item
                            name="started_at"
                            label="Start Date: "
                            rules={[{
                                required: true,
                                message: 'Please enter a start date.'
                            }]}
                        >
                            <DatePicker
                                format={dateFormat}
                                placeholder="DD-MM-YYYY"
                                className="inherit-parent-width"
                                disabledDate={today => today.isBefore(dayjs().subtract(1,'day'))}
                            />
                        </Form.Item>
                    </Row>
                    <Row className="full-width-row">
                        <div className="end-date-container">
                            <Form.Item
                                name="ended_at"
                                label="End Date: "
                                rules={[{
                                    required: radioSelected === 'date',
                                    message: 'Please enter an end date.'
                                }]}
                                className="inherit-parent-width"
                            >
                                <DatePicker
                                    format={dateFormat}
                                    placeholder="DD-MM-YYYY"
                                    className="inherit-parent-width"
                                    disabledDate={today => today.isBefore(dayjs().subtract(1,'day'))}
                                />
                            </Form.Item>
                            <Button
                                type="link"
                                className="end-date-clear-button"
                                onClick={() => {
                                    // Clears End Date field.
                                    form.resetFields(['ended_at']);
                                }}
                            >
                                Clear
                            </Button>
                        </div>
                    </Row>
                    <Row className="full-width-row">
                        <Form.Item
                            name="default_amount"
                            label="Value of Promotional Credit: "
                            rules={[{
                                required: true,
                                type: 'number',
                                min: 0.01,
                                message: greaterThanZero
                            }]}
                        >
                            <InputMoney
                                placeholder="0.00"
                            />
                        </Form.Item>
                    </Row>
                    <Row className="full-width-row">
                        <Form.Item
                            name="promo-credit-expire"
                            label="Promotional Credit Expiration "
                            rules={[{
                                required: true,
                                message: 'Please select an expiry date or duration.'
                            }]}
                        >
                            <Radio.Group
                                className="radio-group"
                                onChange={e => {
                                    // Reset value on radio change.
                                    form.resetFields(['credits_default_ended_at', 'credits_default_duration']);
                                    setRadioSelected(e.target.value);
                                }}
                            >
                                <div className="radio-container">
                                    <Radio
                                        name="exp-date-radio"
                                        className="radio-button"
                                        value="date"
                                    >
                                        Date:
                                    </Radio>
                                    <Form.Item
                                        name="credits_default_ended_at"
                                        className="expiry-form-item"
                                        rules={[{
                                            required: radioSelected === 'date',
                                            message: 'Please select a default expiry date.'
                                        }]}
                                    >
                                        <DatePicker
                                            name="exp-date"
                                            className="inherit-parent-width"
                                            placeholder="DD-MM-YYYY"
                                            format={dateFormat}
                                            disabled={radioSelected === 'duration' || !radioSelected}
                                            disabledDate={today => today.isBefore(dayjs().subtract(1,'day'))}
                                        />
                                    </Form.Item>
                                </div>
                                <div className="radio-container">
                                    <Radio
                                        name="exp-duration-radio"
                                        value="duration"
                                        className="radio-button"
                                    >
                                        Duration:
                                    </Radio>
                                    <Form.Item
                                        name="credits_default_duration"
                                        className="expiry-form-item"
                                        rules={[{
                                            required: radioSelected === 'duration',
                                            message: 'Please enter a duration.'
                                        },
                                        {
                                            type: 'number',
                                            min: 1,
                                            message: greaterThanOrEqualOne
                                        },
                                        {
                                            type: 'number',
                                            max: 1826,
                                            message: 'Please select an expiration date if the duration is more than 5 years.'
                                        }]}
                                    >
                                        <InputNumber
                                            name="exp-duration"
                                            className="inherit-parent-width"
                                            placeholder="Type Here"
                                            disabled={radioSelected === 'date' || !radioSelected}
                                            addonAfter="days"
                                        />
                                    </Form.Item>
                                </div>
                            </Radio.Group>
                        </Form.Item>
                    </Row>
                    <Row className="full-width-row">
                        <Form.Item
                            name="credits_max_num_total"
                            label="Maximum promotional credits: "
                            extra="Please leave it blank if there is no limit."
                            rules={[{
                                required: false,
                                pattern: new RegExp("^[1-9][0-9]*$"), // Must be whole number, min 1.
                                message: wholeNumberGreaterOrEqualToOne
                            }]}
                        >
                            <InputNumber
                                placeholder="Type here"
                                className="inherit-parent-width"
                            />
                        </Form.Item>
                    </Row>
                    <Row className="max-credits-padding">
                        <Form.Item
                            name="credits_max_num_account"
                            label="Maximum promotional credits per account: "
                            extra="Please leave it blank if there is no limit."
                            rules={[{
                                required: false,
                                pattern: new RegExp("^[1-9][0-9]*$"), // Must be whole number, min 1.
                                message: wholeNumberGreaterOrEqualToOne
                            }]}
                        >
                            <InputNumber
                                placeholder="Type here"
                                className="inherit-parent-width"
                            />
                        </Form.Item>
                    </Row>
                    <Row>
                        <Col>
                            <Form.Item>
                                <Button
                                    className="cancel-button"
                                    onClick={onClose}
                                >
                                    Cancel
                                </Button>
                                <Button loading={loading} type="primary" htmlType="submit">Add Promotion</Button>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Drawer>
        </>
    )
}

export default PromotionAddDrawer;
